<script lang="ts">
	import { beforeUpdate } from 'svelte';
	// Stores
	import { firmwares_gen_2 } from 'src/stores/device-runtime/process';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { getDeviceState } from 'src/stores/device-runtime/states';
	import { format } from 'svelte-i18n';

	// Services and configs
	import { devices_config } from 'src/configs/devices';
	import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';

	// Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_control_component_channel_structure,
		is_gen_2,
		isWallDisplay,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { hasAddonPeripherals, getAddonStatus } from 'src/helpers/addons/addon_helpers';

	// Design System Components
	import { ParameterCardV2 } from '@Shelly/shelly-design-system';
	// Helpers
	import { mapIconToThin } from 'src/helpers/new-layout-helpers';

	// Components
	import ControlOnOff from 'src/components/Controls/Single/ControlOnOff.svelte';
	import ControlOnOffAddon from 'src/components/Controls/Single/ControlOnOffAddon.svelte';
	import TimerRemaining from 'src/components/TimerRemaining/TimerRemaining.svelte';
	import ThumbLayerChart from 'src/components/Chart/LayerChart/ThumbLayerChart.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import { useNavigateToHistoryStatisticsTab } from 'src/components/Sidepanel/useNavigateToHistoryStatisticsTab';
	import ExtractedWallDisplayView from './ExtractedWallDisplayView.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';

	// Types
	import type { Device } from 'src/types/Device.ts';

	// Get device data from sidepanel state
	export let device: Device;

	const deviceConfig = devices_config?.[device?.type];
	const isGen2 = is_gen_2(device);

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;

	$: isExtractedDevice = isWallDisplay(device?.type) && device?.category !== 'relay';
	$: isMainWallDisplay = isWallDisplay(device?.type) && device?.category === 'relay';
	$: isThermostatEnabled = isMainWallDisplay
		? typeof settings?.['thermostat:0'] !== 'undefined'
		: false;

	// Status and label processing
	$: _statuses = status?.online && deviceConfig ? status_configurator(status, device, settings) : [];
	$: labelList = Array.isArray(_statuses)
		? _statuses.filter((e) => {
				return 'undefined' === typeof e.main && e.name !== 'state';
			})
		: [];
	$: combinedLabelList = [...Object.values(timerLabels), ...labelList];
	$: mainLabel = Array.isArray(_statuses) ? _statuses.find((i) => i.main) : undefined;

	$: temperatureLabel =
		isThermostatEnabled && Array.isArray(_statuses)
			? _statuses.find(
					(i) =>
						i.name === 'temperature' ||
						i.name === 'thermostat_temperature' ||
						i.peripheralType === 'temperature',
				)
			: undefined;

	// Charts
	$: deviceChartsConfig = device ? getDeviceChartsConfig(device) : [];

	const { navigateToHistoryStatisticsTab } = useNavigateToHistoryStatisticsTab();

	const handleOnClickThumbChart = (event: CustomEvent | Event) => {
		navigateToHistoryStatisticsTab();
	};

	const handleParameterCardClick = () => {
		const label = mainLabel || temperatureLabel;
		if (
			label?.name === 'temperature' ||
			label?.name === 'thermostat_temperature' ||
			label?.peripheralType === 'temperature'
		) {
			navigateToHistoryStatisticsTab();
		}
	};

	// Add-on peripherals (for detecting switch add-on)
	$: addonPeripherals = hasAddonPeripherals(device) ? getAddonStatus(device, [], false, true) : [];
	$: switchAddon = addonPeripherals.find((p) => p.peripheralType === 'switch');

	let timerLabels: Record<string, any> = {};
	let timer = false;
	let deviceState: any = getDeviceState(device?.id);

	beforeUpdate(() => {
		if (status?.online && deviceConfig) {
			timer =
				isGen2 && status?.[`${get_gen_2_component_channel_structure(device)}`]?.timer_started_at;
		}
		deviceState = getDeviceState(device?.id);
	});
</script>

{#if isExtractedDevice}
	<ExtractedWallDisplayView {device} />
{:else}
	<DeviceViewStateWrapper {device}>
		<!-- Normal relay device - staticTop -->
		<div class="label-list-wrapper mb-s" slot="staticTop">
			{#if timer}
				{#key status[get_gen_2_control_component_channel_structure(device)].timer_started_at}
					<TimerRemaining timerKey="gen2_main" {device} {status} {settings} bind:timerLabels />
				{/key}
			{/if}
			<DeviceLabelList labelList={combinedLabelList} isInteractive={false} />
			<DeviceNotificationLabel deviceId={device?.id} />
		</div>

		<!-- Normal relay device - staticBottom -->
		<div
			class="flex-full-height justify-content-end gap-s"
			slot="staticBottom"
			style="margin-top: auto;"
		>
			<div class="flex-full-height justify-content-end">
				{#if deviceChartsConfig.length}
					<ThumbLayerChart {device} on:click={handleOnClickThumbChart} />
				{/if}

				{#if devices_config?.[device?.type]?.isPM}
					<!-- <PeakPowerInfoV2 {device} /> -->
					<ParameterCardV2
						size="s"
						class="mb-s"
						icon={mapIconToThin('fas fa-plug')}
						title={Array.isArray(_statuses)
							? _statuses.filter((s) => s.name === 'power')[0]?.value
							: undefined}
						description={capFirstChar($format('collapse_title_power', { default: 'Power' }))}
					/>
				{/if}
			</div>

			{#if isThermostatEnabled}
				{#if mainLabel}
					<ParameterCardV2
						{device}
						{status}
						{settings}
						{mainLabel}
						icon={mapIconToThin(mainLabel.icon)}
						iconColor={mainLabel?.iconColor}
						title={capFirstChar(mainLabel.value)}
						description={capFirstChar(mainLabel.translation)}
						backgroundColor="--color-0"
						isFocusable
						class={mainLabel?.name === 'temperature' ||
						mainLabel?.peripheralType === 'temperature'
							? 'cursor-pointer'
							: ''}
						on:click={handleParameterCardClick}
					/>
				{/if}
			{:else if !isThermostatEnabled}
				{#if switchAddon}
					<div class="d-flex gap-s">
						<div class="control-flex-column flex-1">
							<ControlOnOffAddon
								{device}
								{status}
								isInSlot
								useV2Components={true}
								component={switchAddon.sensor?.component}
							/>
						</div>
						<div class="control-flex-column flex-1">
							<ControlOnOff {device} {status} {settings} isInSlot useV2Components={true} />
						</div>
					</div>
				{:else}
					<ControlOnOff
						{device}
						{status}
						{settings}
						isWide
						useV2Components={true}
						deviceButtonV2Props={{ size: 'l' }}
					/>
				{/if}
			{/if}
		</div>
	</DeviceViewStateWrapper>
{/if}
